import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { AccountAddress } from '@/lib/api-types'
import { formatDate, formatMoney } from '@/lib/format'
import { clearUser } from '@/lib/observability'
import { creditAppChipClasses, creditAppStatusLabel } from '@/lib/status'
import { cn } from '@/lib/utils'
import { RETURN_TO_KEY, useSessionStore } from '@/stores/session'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DescriptionList } from '@/components/ui/description-list'
import { ErrorState } from '@/components/ui/error-state'
import { PageHeader } from '@/components/ui/page-header'
import { SectionHeader } from '@/components/ui/section-header'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { StatusChip } from '@/components/ui/status-chip'

export function AccountPage() {
  const contact = useSessionStore((s) => s.contact)
  const logout = useSessionStore((s) => s.logout)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['account'],
    queryFn: ({ signal }) => api.getAccount({ signal }),
    staleTime: 5 * 60_000,
  })

  function signOut() {
    try {
      window.localStorage.removeItem(RETURN_TO_KEY)
    } catch {
      // non-fatal
    }
    logout()
    clearUser()
    queryClient.clear()
    navigate('/login')
  }

  if (isPending) {
    return (
      <div className="flex justify-center py-24">
        <Spinner label="Loading your account…" />
      </div>
    )
  }

  if (isError) {
    return <ErrorState message="We couldn’t load your account." onRetry={() => void refetch()} />
  }

  const shipping = data.addresses.filter((a) => a.kind === 'shipping')
  const billing = data.addresses.filter((a) => a.kind === 'billing')

  return (
    <div className="mx-auto max-w-data">
      <PageHeader
        title={data.name}
        description="Your terms and addresses on file with Green Line. To change any of this, reach out to your rep."
        actions={
          <Button asChild variant="quiet" size="sm">
            <Link to="/getting-started">Read the getting-started guide</Link>
          </Button>
        }
      />

      {/* Profile facts */}
      <Card className="mt-8">
        <DescriptionList
          columns={4}
          items={[
            { label: 'Customer code', value: data.cardCode ?? '—', mono: true },
            { label: 'Payment terms', value: data.paymentTerms ?? '—' },
            {
              label: 'Credit limit',
              value: data.creditLimit != null ? formatMoney(data.creditLimit) : '—',
              mono: true,
            },
            {
              label: 'Minimum shelf life',
              value: data.minimumShelfLifeDays != null ? `${data.minimumShelfLifeDays} days` : '—',
            },
          ]}
        />
      </Card>

      {/* Credit */}
      <section aria-labelledby="account-credit-heading" className="mt-10">
        <SectionHeader id="account-credit-heading" title="Credit" />
        <Card className="mt-3">
          <CreditSection />
        </Card>
      </section>

      {/* Addresses */}
      <section aria-labelledby="account-addresses-heading" className="mt-10">
        <SectionHeader id="account-addresses-heading" title="Addresses" />
        {data.addresses.length === 0 ? (
          <p className="mt-4 text-sm text-mute">No addresses on file yet.</p>
        ) : (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[...shipping, ...billing].map((address) => (
              <AddressCard key={address.id} address={address} />
            ))}
          </div>
        )}
      </section>

      {/* Colleagues */}
      <section aria-labelledby="account-colleagues-heading" className="mt-10">
        <SectionHeader id="account-colleagues-heading" title="Hub access" />
        <p className="mt-2 text-sm text-mute">
          These colleagues also have Hub access for {data.name}.
        </p>
        <Card padding="none" className="mt-3">
          <ul className="divide-y divide-rule/40">
            {data.colleagues.map((colleague) => (
              <li
                key={colleague.id}
                className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-3"
              >
                <span className="text-sm font-medium text-ink">
                  {colleague.name}
                  {colleague.isYou ? (
                    <span className="ml-2 rounded-xs border border-brand/30 bg-brandTint px-1.5 py-0.5 font-mono text-[0.625rem] uppercase tracking-eyebrow text-brandDeep">
                      You
                    </span>
                  ) : null}
                </span>
                <span className="font-mono text-xs text-mute">{colleague.email}</span>
              </li>
            ))}
          </ul>
        </Card>
      </section>

      {/* Session */}
      <section aria-labelledby="account-session-heading" className="mt-10">
        <SectionHeader id="account-session-heading" title="Session" />
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-inkMid">
            Signed in as{' '}
            <span className="font-medium text-ink">
              {contact ? `${contact.firstName} ${contact.lastName}` : '—'}
            </span>{' '}
            ({contact?.email}).
          </p>
          <Button variant="neutral" size="sm" onClick={signOut}>
            Sign out
          </Button>
        </div>
      </section>
    </div>
  )
}

/**
 * The Credit entry point: no application yet → the apply CTA; an application
 * on file → its status chip, sent date, and a quiet link to the detail page.
 */
function CreditSection() {
  const { data, isPending, isError } = useQuery({
    queryKey: ['credit-application'],
    queryFn: ({ signal }) => api.getCreditApplication({ signal }),
  })

  if (isPending) {
    return <Skeleton className="h-3.5 w-56" />
  }

  if (isError) {
    return <p className="text-sm text-mute">We couldn’t load your credit application right now.</p>
  }

  const application = data.application

  if (!application) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-inkMid">Set up credit terms with Green Line.</p>
        <Button asChild size="sm">
          <Link to="/credit/apply">Start a credit application</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <StatusChip
          label={creditAppStatusLabel(application.status)}
          chipClasses={creditAppChipClasses(application.status)}
        />
        <p className="text-sm text-inkMid">
          Application <span className="font-mono text-[0.8125rem]">{application.name}</span> · sent{' '}
          {formatDate(application.submittedAt)}
        </p>
      </div>
      <Button asChild variant="quiet" size="sm">
        <Link to="/credit">View application</Link>
      </Button>
    </div>
  )
}

function AddressCard({ address }: { address: AccountAddress }) {
  return (
    <Card padding="sm" className={cn(address.isDefaultShipTo && 'border-brand/40')}>
      <div className="flex items-center justify-between gap-2">
        <span className="micro-label">{address.kind === 'shipping' ? 'Shipping' : 'Billing'}</span>
        {address.isDefaultShipTo ? (
          <span className="rounded-xs border border-brand/30 bg-brandTint px-1.5 py-0.5 font-mono text-[0.625rem] uppercase tracking-eyebrow text-brandDeep">
            Default ship-to
          </span>
        ) : null}
      </div>
      <p className="mt-2 text-sm font-medium text-ink">{address.street ?? '—'}</p>
      <p className="mt-0.5 text-sm text-inkMid">
        {[address.city, address.state, address.postalCode].filter(Boolean).join(', ')}
      </p>
      {address.country ? <p className="mt-0.5 text-sm text-mute">{address.country}</p> : null}
      <p className="mt-2 font-mono text-xs text-mute">{address.code}</p>
    </Card>
  )
}
